import React, { useEffect, useState } from 'react';
import { getBetHistory } from '../api/api';
import { TicketOut } from '../api/types';

interface DesktopMyBetsPageProps {
  onLoginClick: () => void;
}

const DesktopMyBetsPage: React.FC<DesktopMyBetsPageProps> = ({ onLoginClick }) => {
  const [tickets, setTickets] = useState<TicketOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'open' | 'won' | 'lost' | 'cashed_out'>('all');
  const isLogged = !!localStorage.getItem('access_token');

  useEffect(() => {
    if (isLogged) {
      setLoading(true);
      getBetHistory()
        .then((data) => setTickets(data))
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setTickets([]);
    }
  }, [isLogged]);

  if (!isLogged) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Meine Wetten</h2>
        <p className="text-gray-500 mb-6">Bitte melde dich an, um deine Wetten zu sehen.</p>
        <button onClick={onLoginClick} className="bg-tipico-red text-white px-8 py-3 rounded text-sm font-bold shadow hover:bg-tipico-red-dark transition">Anmelden</button>
      </div>
    );
  }

  const filteredTickets = tickets.filter(t => activeTab === 'all' ? true : t.status === activeTab);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden flex flex-col">
      <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
        <h1 className="text-2xl font-black text-gray-900">Meine Wetten</h1>
      </div>

      <div className="flex border-b border-gray-200 px-6 pt-4 gap-6 bg-white shrink-0">
        {[
          { id: 'all', label: 'Alle' },
          { id: 'open', label: 'Offene Wetten' },
          { id: 'won', label: 'Gewonnen' },
          { id: 'lost', label: 'Verloren' },
          { id: 'cashed_out', label: 'Cashout' },
        ].map((tab) => (
           <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`pb-3 text-sm font-bold transition-all border-b-[3px] ${
              activeTab === tab.id
                ? 'border-tipico-red text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="p-6 flex-1 flex flex-col min-h-[400px]">
        {loading ? (
           <div className="flex-1 flex items-center justify-center">
             <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
           </div>
        ) : filteredTickets.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center">
            <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-gray-500 font-medium">Du hast keine {activeTab !== 'all' && activeTab} Wetten in diesem Bereich.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
             {filteredTickets.map(t => (
               <div key={t.id} className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition">
                 <div className="flex justify-between items-center bg-gray-50 px-4 py-3 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                       <span className="font-bold text-sm text-gray-900 uppercase tracking-wide">{t.ticket_type === 'kombi' ? 'Kombiwette' : 'Einzelwette'}</span>
                       <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${
                         t.status === 'won' ? 'bg-green-100 text-green-700' :
                         t.status === 'lost' ? 'bg-red-100 text-red-700' :
                         t.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-700'
                       }`}>
                         {t.status === 'won' ? 'Gewonnen' : t.status === 'lost' ? 'Verloren' : t.status === 'open' ? 'Offen' : 'Ausgezahlt'}
                       </span>
                    </div>
                    <div className="text-right flex items-center gap-8">
                       <div className="flex flex-col">
                          <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Einsatz</span>
                          <span className="font-bold text-[13px] text-gray-900">{parseFloat(t.total_stake).toFixed(2)} €</span>
                       </div>
                       <div className="flex flex-col text-right">
                          <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Gewinn</span>
                          <span className="font-bold text-[13px] text-[#00a650]">{t.status === 'won' || t.status === 'cashed_out' ? (parseFloat(t.potential_payout)).toFixed(2) : t.status === 'lost' ? '0.00' : parseFloat(t.potential_payout).toFixed(2)} €</span>
                       </div>
                    </div>
                 </div>
                 <div className="px-4 py-3 text-sm text-gray-600 flex flex-col gap-3">
                   {t.legs && t.legs.map((leg: any, idx: number) => (
                     <div key={idx} className="flex justify-between items-start pt-2 first:pt-0 border-t first:border-0 border-dashed border-gray-100">
                       <div className="flex flex-col">
                          <span className="font-bold text-gray-800">{leg.event_name}</span>
                          <span className="text-[11px] text-gray-500 mt-0.5">{leg.market_name} - Tipp: <strong className="text-gray-700">{leg.outcome_name}</strong></span>
                       </div>
                       <span className="font-bold text-sm">{parseFloat(leg.odds).toFixed(2)}</span>
                     </div>
                   ))}
                 </div>
                 
               </div>
             ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopMyBetsPage;
